import { combineReducers } from 'redux'
import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { ByUserData, ByUserQuery, StatisticsData } from '../../../responses/statistics'
import { none, of, Option } from 'fp-ts/Option'
import { RootEpic } from '../../root'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import {
    HideConfiguration,
    OpenConfiguration,
    SortConfiguration
} from '../../../components/pages/statistics/StatisticsTable/configuration'
import { ID } from '../../../types/UtilityTypes'
import { User } from '../../../responses/users'
import { DepartmentWithUsers } from '../../../responses/departments'
import { payloadReducer } from '../../../utils/redux-utils'
import { SortDirection } from '../../../constants/direction'
import {RequestStatus} from '../../../responses/response'

export const byUserActions = {
    data: createAsyncAction(
        '@STATISTICS_BY_USER/DATA_REQUEST',
        '@STATISTICS_BY_USER/DATA_SUCCESS',
        '@STATISTICS_BY_USER/DATA_FAILURE',
    )<ByUserQuery, StatisticsData<ByUserData>, void>(),
    setHiddenColumns: createAction('@STATISTICS_BY_USER/SET_HIDDEN_COLUMNS')<HideConfiguration[]>(),
    setManagers: createAction('@STATISTICS_BY_USER/SET_MANAGERS')<User[]>(),
    setDepartments: createAction('@STATISTICS_BY_USER/SET_DEPARTMENTS')<DepartmentWithUsers[]>(),
    setUsers: createAction('@STATISTICS_BY_USER/SET_USERS')<User[]>(),
    setSort: createAction('@STATISTICS_BY_USER/SET_SORT')<SortConfiguration>(),
    toggleRow: createAction('@STATISTICS_BY_USER/TOGGLE_ROW')<Record<ID, OpenConfiguration>>(),
    clearFilters: createAction('@STATISTICS_BY_MANAGER/CLEAR_FILTERS')()
}

export interface ByUserState {
    data: Option<StatisticsData<ByUserData>>,
    hide: HideConfiguration[],
    open: Record<ID, OpenConfiguration>,
    sort: SortConfiguration,
    managers: User[],
    users: User[],
    departments: DepartmentWithUsers[],
    state: RequestStatus
}

export const byUserReducer = combineReducers<ByUserState>({
    data: createReducer<Option<StatisticsData<ByUserData>>>(none)
        .handleAction(byUserActions.data.success, (_, action) => of(action.payload)),
    hide: createReducer<HideConfiguration[]>([])
        .handleAction(byUserActions.setHiddenColumns, payloadReducer),
    open: createReducer<Record<ID, OpenConfiguration>>({})
        .handleAction(byUserActions.data.success, () => ({}))
        .handleAction(byUserActions.toggleRow, payloadReducer),
    sort: createReducer<SortConfiguration>({group: '', name: '', direction: SortDirection.ASC})
        .handleAction(byUserActions.setSort, payloadReducer),
    managers: createReducer<User[]>([])
        .handleAction(byUserActions.setManagers, payloadReducer)
        .handleAction(byUserActions.clearFilters, () => []),
    users: createReducer<User[]>([])
        .handleAction(byUserActions.setUsers, payloadReducer)
        .handleAction(byUserActions.clearFilters, () => []),
    departments: createReducer<DepartmentWithUsers[]>([])
        .handleAction(byUserActions.setDepartments, payloadReducer)
        .handleAction(byUserActions.clearFilters, () => []),
    state: createReducer<RequestStatus>(RequestStatus.INITIAL)
        .handleAction(byUserActions.data.request, () => RequestStatus.FETCHING)
        .handleAction(byUserActions.data.success, () => RequestStatus.SUCCESS)
        .handleAction(byUserActions.data.failure, () => RequestStatus.FAILURE),
})

export const byUserEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byUserActions.data.request)),
    mergeMap(action => apiRequests.statistics.byUser({query: action.payload})),
    map(res => res.response.success?
        byUserActions.data.success(res.response):
        byUserActions.data.failure(toastFailedResponse(res.response, 'Не удалось загрузить статистику по исполнителям'))
    )
)